<template>
    <div id="rent-or-buy-calculator" class="calculator__calibre">
        <div :class="{'+pd-sm +pd-bottom-none': isMobile}">
            <h1 class="+heading-delta +mg-bottom-none" v-if="isMobile">Rent Vs. Buy Calculator</h1>
            <p v-if="isMobile">
                For some people, buying their home makes the most sense, and for others, renting is best. To determine which is right for you, you first need to determine whether you can afford to buy. Then you need to consider other factors, including the time you'll stay in your new home, the home's prospects for appreciation and taxes. Just answer the following questions and we'll advise you on what seems best for you.
            </p>
            <p v-else class="+font-calibre-regular">The age-old question of whether to rent vs. buy doesn’t have a simple answer. In reality, many factors come into the equation, such as your finances, family and job goals, market conditions and other considerations.</p>
        </div>
        <div class="calculator__container calculator__gray-bg +pd-sm">
            <div>
                <p>First, we'll consider whether you can afford to buy a home right now.</p>
                Which best describes your access to a down payment?
            </div>
            <div>
                <label class="custom-input --radio --regular-weight">
                    I have no savings and no access to a lump sum.
                    <input type="radio" value="s" name="accessToDownPayment" v-model="accessToDownPayment">
                    <div class="custom-input__indicator"></div>
                </label>
            </div>
            <div>
                <label class="custom-input --radio --regular-weight">
                    I have some savings, access to a modest lump sum, or both.
                    <input type="radio" value="m" name="accessToDownPayment" v-model="accessToDownPayment">
                    <div class="custom-input__indicator"></div>
                </label>
            </div>
            <div>
                <label class="custom-input --radio --regular-weight">
                    I have access to a substantial amount of money.
                    <input type="radio" value="l" name="accessToDownPayment" v-model="accessToDownPayment">
                    <div class="custom-input__indicator"></div>
                </label>
            </div>

            <div v-if="accessToDownPayment !== 's'">
                <div class="+mg-top" ref="debt">
                    <div>
                        Approximately how much consumer debt do you have? <br/>
                        (Include credit cards and personal loans, but not student loans and any current mortgage)
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            None to little debt.
                            <input type="radio" name="debt" value="s" v-model="debt">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            A moderate amount.
                            <input type="radio" name="debt" value="m" v-model="debt">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            A lot.
                            <input type="radio" name="debt" value="l" v-model="debt">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                </div>

                <div class="+mg-top">
                    <div>
                        Based on your current savings, what percentage of the home's purchase price can you put down as a down payment?<br/>
                        Enter a percentage value. If you don't know exactly, guess as best you can.
                    </div>
                    <div class="+mg-bottom-xs +mg-top-xs" :class="{'grid': !isMobile}">
                        <div :class="{'grid-cell size-1of2 +pd-right-xxs' : !isMobile}">
                            <numeric-input
                                    :title="'Down Payment'"
                                    :show-label=false
                                    :auto-validate=true
                                    :name="'downPayment'"
                                    :tracking-enabled="false"
                                    :error="'Invalid down payment percentage'"
                                    :min-value="config.downPayment.min"
                                    :max-value="config.downPayment.max"
                                    :suffix="'%'"
                                    :model="downPayment"
                                    v-model="downPayment"
                                    :has-bottom-margin=false
                                    :fire-blur=true
                                    @blur="hideResults()"
                                    ref="downPayment"
                            ></numeric-input>
                        </div>
                    </div>
                </div>

                <div class="+mg-top" v-if="Number(downPayment) < 95" ref="creditHistory">
                    <div>
                        Which of the following best describes your credit history?
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Poor
                            <input type="radio" name="creditHistory" value="s" v-model="creditHistory">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Pretty good
                            <input type="radio" name="creditHistory" value="m" v-model="creditHistory">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Excellent
                            <input type="radio" name="creditHistory" value="l" v-model="creditHistory">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                </div>

                <div class="+mg-top">
                    <div>
                        How long do you think you'll stay in your new home?
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Less than one year.
                            <input type="radio" name="lengthOfStay" value="s" v-model="lengthOfStay">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            More than a year but probably not more then five years.
                            <input type="radio" name="lengthOfStay" value="m" v-model="lengthOfStay">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Pretty sure I'll be there more than five years.
                            <input type="radio" name="lengthOfStay" value="l" v-model="lengthOfStay">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                </div>

                <div class="+mg-top">
                    <div>
                        How are home prices in the area in which you plan to live?
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Prices don't change that much over time.
                            <input type="radio" name="location" value="s" v-model="location">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Prices are rising, but they seem reasonable.
                            <input type="radio" name="location" value="m" v-model="location">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Housing prices are out of control.
                            <input type="radio" name="location" value="l" v-model="location">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                </div>

                <div class="+mg-top">
                    <div>
                        If you get a mortgage and you itemize, you will probably be able to deduct the interest you pay. Does this deduction apply to you?
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Yes, I probably will be able to deduct the mortgage interest.
                            <input type="radio" name="tax" value="s" v-model="tax">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            No, I probably won't be able to deduct the mortgage interest.
                            <input type="radio" name="tax" value="m" v-model="tax">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                </div>

                <div class="+mg-top">
                    <div>
                        Which best describes your monthly budget?
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            Things are tight after I pay all my bills.
                            <input type="radio" name="budget" value="s" v-model="budget">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            I usually have some money left at the end of the month.
                            <input type="radio" name="budget" value="m" v-model="budget">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            I have a fairly flexible budget.
                            <input type="radio" name="budget" value="l" v-model="budget">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                </div>

                <div class="+mg-top">
                    <div>
                        Which of the following is most true for you?
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            I'm willing to put in the extra effort required to maintain a home that I own.
                            <input type="radio" name="maintenance" value="s" v-model="maintenance">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            I'd prefer to let someone else worry about maintaining my home.
                            <input type="radio" name="maintenance" value="m" v-model="maintenance">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                </div>

                <div class="+mg-top">
                    <div>
                        Which best describes your current viewpoint regarding where you live?
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            I'm willing to "settle down" in one place for a while.
                            <input type="radio" name="lifestyle" value="s" v-model="lifestyle">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                    <div>
                        <label class="custom-input --radio --regular-weight">
                            I'd really like the freedom to "pick up and go" when I want.
                            <input type="radio" name="lifestyle" value="m" v-model="lifestyle">
                            <div class="custom-input__indicator"></div>
                        </label>
                    </div>
                </div>
            </div>

            <div class="grid">
                <div class="grid-cell" :class="{'size-1of3 +center-content': !isMobile, 'size-1of1': isMobile}">
                    <div class='form-input --has-error +pd-bottom' id="calculatorErrors" v-show="hasError">
                        <div class="error-message" v-html="config.errorMessage"></div>
                    </div>
                    <button class="button --primary --beta +mg-top-sm --full-width"
                            v-on:click.prevent="calculate"
                            ref="calculate">
                        Calculate
                    </button>
                </div>
            </div>
        </div>
        <div v-show="resultsVisible" id="calculatorResults" class="calculator__results-section +pd-sm +border +border-top-none">
            <div v-if="accessToDownPayment === 's'">
                <h5>Recommendation:</h5>
                <p>{{ recommendation }}</p>

                <h5>Here's why:</h5>
                <p>{{ resultCopy('accessToDownPayment') }}</p>

                <h5>Things to Consider:</h5>
                <p>{{ resultConsideration('accessToDownPayment') }}</p>
            </div>
            <div v-else>
                <h5>Recommendation:</h5>
                <p>{{ recommendation }} The factors we considered in formulating this recommendation are explained below:</p>

                <h5>Your length of stay:</h5>
                <p>{{ resultCopy('lengthOfStay') }}</p>

                <h5>The location of your home:</h5>
                <p>{{ resultCopy('location') }}</p>

                <h5>Tax implications:</h5>
                <p>{{ resultCopy('tax') }}</p>

                <h5>Your monthly budget:</h5>
                <p>{{ resultCopy('budget') }}</p>

                <h5>Your ability to afford:</h5>
                <p>{{ affordability }}</p>

                <h5>Your attitude toward maintenance:</h5>
                <p>{{ resultCopy('maintenance') }}</p>

                <h5>Your lifestyle preference:</h5>
                <p>{{ resultCopy('lifestyle') }}</p>
            </div>
        </div>
        <p v-if="isMobile" class="+font-calibre-regular +pd-sm ">Our rent vs. buy calculator walks you through key questions -- about your finances and credit, budget, how long you plan to stay in the home, market affordability, your attitude toward maintenance and lifestyle preferences -- to help you decide whether you should rent vs. buy.</p>
    </div>
</template>

<script>
    import config from './config';
    import utils from 'js/bankrate-utilities';
    import numericInput from 'apps/legacy-components/numeric-input';
    import calculatorEventBus from 'apps/bankrate-calculators/events';
    import VueScrollTo from 'vue-scrollto';

    export default {
        components: { numericInput },
        data() {
            return {
                config: config,

                // Inputs
                accessToDownPayment: 's',
                debt: 's',
                downPayment: '',
                creditHistory: 's',
                lengthOfStay: 's',
                location: 's',
                tax: 's',
                budget: 's',
                maintenance: 's',
                lifestyle: 's',

                // Outputs
                recommendBuy: false,
                score: 0,

                // Step
                resultsVisible: false,

                //
                hasError: false
            };
        },
        methods: {
            calculate() {
                if (this.accessToDownPayment === 's') {
                    calculatorEventBus.$emit('calculator:calculation');
                    this.resultsVisible = true;
                    this.$nextTick(function () {
                        this.scrollToResults();
                    });
                } else {
                    this.hasError = false;
                    if (this.$refs.downPayment.hasDisplayError() || this.$refs.downPayment.isBlank("Please set a down payment percentage")) {
                        this.resultsVisible = false;
                        this.$nextTick(function () {
                            this.hasError = true;
                        });
                    } else {
                        let totalWeight = 0;
                        totalWeight += config.lengthOfStay.weight;
                        totalWeight += config.location.weight;
                        totalWeight += config.tax.weight;
                        totalWeight += config.budget.weight;
                        totalWeight += config.maintenance.weight;
                        totalWeight += config.lifestyle.weight;

                        let score = 0;
                        score += config.lengthOfStay.values[this.lengthOfStay].score * config.lengthOfStay.weight;
                        score += config.location.values[this.location].score * config.location.weight;
                        score += config.tax.values[this.tax].score * config.tax.weight;
                        score += config.budget.values[this.budget].score * config.budget.weight;
                        score += config.maintenance.values[this.maintenance].score * config.maintenance.weight;
                        score += config.lifestyle.values[this.lifestyle].score * config.lifestyle.weight;
                        this.score = score / totalWeight;

                        this.resultsVisible = true;
                        this.$nextTick(function () {
                            this.scrollToResults();
                        });
                    }
                }
            },

            resultCopy(name) {
                return typeof config[name].values[this[name]] === 'object' ? config[name].values[this[name]].copy : '';
            },

            resultConsideration(name) {
                return typeof config[name].values[this[name]] === 'object' ? config[name].values[this[name]].consideration : '';
            },

            scrollToResults() {
                VueScrollTo.scrollTo('#calculatorResults', 500, {offset: 0});
            },
            hideResults() {
                this.resultsVisible = false;
            }
        },
        computed: {
            recommendation() {
                if (this.score === 0) {
                    return config.recommendation.noDownPayment;
                } else if (this.score > 3.6) {
                    this.recommendBuy = true;
                    return config.recommendation.buy;
                } else if (this.score > 2.6) {
                    this.recommendBuy = true;
                    return config.recommendation.either;
                } else if (this.score > 2) {
                    return config.recommendation.either;
                }
                return config.recommendation.rent;
            },
            affordability() {
                if (this.recommendBuy) {
                    return config.affordability.buy;
                } else {
                    return config.affordability.rent;
                }
            },
            isMobile() {
                return utils.isMobile();
            }
        },

        watch: {
            accessToDownPayment() {
                this.resultsVisible = false;
            },
            debt() {
                this.resultsVisible = false;
            },
            creditHistory() {
                this.resultsVisible = false;
            },
            lengthOfStay() {
                this.resultsVisible = false;
            },
            location() {
                this.resultsVisible = false;
            },
            tax() {
                this.resultsVisible = false;
            },
            budget() {
                this.resultsVisible = false;
            },
            maintenance() {
                this.resultsVisible = false;
            },
            lifestyle() {
                this.resultsVisible = false;
            }
        }
    }
</script>
